import { ChangeDetectionStrategy, Component, computed, inject, input, output, TemplateRef } from '@angular/core';

import { v4 as uuid } from 'uuid';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { ButtonTheme } from '../buttons/button/enums';
import { ModalButtons } from '../modal/interfaces';
import { NotificationLayout, NotificationScale, NotificationTheme } from '../notifications/enums';
import { PopoverOptions } from '../popover/popover.interface';
import { PopoverPosition } from '../popover/popover.enum';
import { PopoverService } from '../popover/service/popover.service';

@Component({
  selector: 'fcom-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FiltersComponent {
  readonly ButtonTheme = ButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationScale = NotificationScale;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly popoverService = inject(PopoverService);
  private readonly themeService = inject(FinnairThemeService);

  readonly primaryFooterButtonTheme = computed(() =>
    this.themeService.siteTheme() === FinnairTheme.LEGACY
      ? ButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE
      : ButtonTheme.PRIMARY_ACCENT
  );

  expandButtonId = uuid();

  /** True on mobile and tablet screens (≤1023px). Managed internally. */
  readonly isMobileOrTablet = this.mediaQueryService.isBreakpoint('tablet_down');
  readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');

  /** TemplateRef provided via `[filterContent]="myTemplateRef"` by the caller. */
  readonly filterContent = input<TemplateRef<unknown>>();

  disabled = input(false);
  buttonText = input<string>();
  hasFiltering = input(false);
  numberToShowWhenFiltered = input<number | undefined>(undefined);
  primaryFooterButtonTitle = input<string>();
  modalTitle = input<string>();
  notificationContent = input<string>();
  isModalOpen = input<boolean>(false);
  displayNotification = input<boolean>(false);
  secondaryFooterButtonTitle = input<string>();
  /**
   * Toggle view where the desktop has a Chevron down icon.
   * Default (false) has the Filter options icon on all breakpoints
   */
  dropdownVisual = input<boolean>(false);
  /**
   * When true, forces the modal on desktop/laptop breakpoints instead of the default popover.
   * Has no effect on mobile/tablet where the modal is always used.
   */
  modalInDesktop = input(false);
  /**
   * Optional context string for the popover. Enables callers to close this popover
   * programmatically via `PopoverService.closeByContext(context)`.
   */
  popoverContext = input<string>();

  /**
   * True when a popover should be shown instead of a modal.
   * Active on laptop/desktop breakpoints when `modalInDesktop` is false.
   */
  readonly usePopover = computed(() => !this.isMobileOrTablet() && !this.modalInDesktop());

  readonly popoverOptions = computed<PopoverOptions>(() => ({
    popoverID: `filters-popover-${this.expandButtonId}`,
    enableOnClick: true,
    openByDefault: false,
    showCloseButton: false,
    showArrowCaret: false,
    showLeftBorder: false,
    popoverPosition: PopoverPosition.BOTTOM,
    roundedCorners: true,
    hasPadding: false,
    ...(this.popoverContext() ? { context: this.popoverContext() } : {}),
  }));

  primaryFooterButtonClick = output<void>();
  secondaryFooterButtonClick = output<void>();
  filtersOpen = output<void>();
  filtersClose = output<void>();

  onButtonClick(): void {
    this.filtersOpen.emit();
  }

  onPrimaryButtonClick(): void {
    this.primaryFooterButtonClick.emit();
    if (this.usePopover()) {
      this.popoverService.close(true);
      this.filtersClose.emit();
    }
  }

  onSecondaryButtonClick(): void {
    this.secondaryFooterButtonClick.emit();
    if (this.usePopover()) {
      this.popoverService.close(true);
      this.filtersClose.emit();
    }
  }

  onPopoverClose(): void {
    if (this.usePopover()) {
      this.filtersClose.emit();
    }
  }
}
